/**
 * CATÁLOGO TÉCNICO — la costura entre lo que manda la física y lo que decide Juan.
 * =============================================================================
 *
 * Este archivo existe por una frase de Juan del 2026-08-14:
 *
 *   *«el modelo de cable que voy a usar, esas características, todo eso debe quedar
 *   abierto para que yo sea capaz de usar diferentes tipos de cable y cambiar mis gastos
 *   fijos, ya que esto se va a ir moviendo y debe ser dinámico.»*
 *
 * Tenía razón, y el cotizador tenía las dos mitades separadas sin nada en medio:
 *
 *   · `electrico.js` y `riego.js` deciden **qué se necesita** —12 AWG, tubo de ¾",
 *     boquilla 15-VAN, bomba CPm 610— y eso sale de tablas de norma y fichas de
 *     fabricante. No es negociable ni editable: es ley y es física.
 *   · `catalogo.js` guarda **qué vende Juan y a cuánto**, y eso ya vivía en el almacén,
 *     ya era editable desde Configuración, y ya se movía.
 *
 * Faltaba que se hablaran. El motor eléctrico decía «12 AWG» y esa cadena no llegaba a
 * ningún precio; el de riego producía una lista de material perfecta que no entraba a
 * ninguna cotización.
 *
 * ---------------------------------------------------------------------------
 * LA REGLA QUE ORDENA TODO ESTE ARCHIVO
 *
 * **El motor elige POR FÍSICA, y cobra CON EL CATÁLOGO DE JUAN.**
 *
 * Nunca al revés. Si Juan sube el precio del cable, la cotización sube. Si Juan cambia de
 * THW a THHN, cambia el precio **y cambia el tubo**, porque el THHN es mucho más delgado y
 * la Tabla 5 del capítulo 10 lo dice. Lo que Juan **no** puede mover es la ampacidad de un
 * 12 AWG ni el factor de relleno: esos no son suyos, son de la norma.
 *
 * ---------------------------------------------------------------------------
 * CÓMO SE ENGANCHA UN INSUMO
 *
 * Los insumos ya vivían en el almacén con `sku`, `nombre`, `tipo` y `costo`. Aquí se les
 * agrega un campo `tec` que dice **qué son técnicamente**:
 *
 *     { sku: 'CBL-12-THW', nombre: 'Cable THW cal. 12 (m)', costo: 22,
 *       tec: { clase: 'cable', awg: '12', aislamiento: 'thw' } }
 *
 * El motor no pide un SKU. Pide *«un cable del 12»* y `resolver()` busca en el catálogo
 * VIVO —el que Juan editó, no el que está escrito aquí— cuál corresponde.
 *
 * Consecuencia deliberada: **agregar una marca nueva no toca este archivo.** Juan da de
 * alta el insumo en Configuración con su `tec`, y el motor lo encuentra solo.
 * ============================================================================= */

/* ============================================================================
   1. PREFERENCIAS — las decisiones de Juan que cambian un cálculo
   ========================================================================== */

/**
 * Estas NO son constantes: son el valor inicial de algo que vive en el almacén y que
 * Juan cambia desde Configuración. Aquí solo están para sembrar la primera vez.
 *
 * Cada una lleva `porque` porque cambia un número de la cotización, y quien la mueva
 * merece saber qué va a mover.
 */
export const PREFERENCIAS_BASE = {
  /**
   * Tipo de aislamiento del conductor. **Cambia el diámetro del tubo**, no solo el precio:
   * un 12 AWG THW ocupa 22.77 mm² y el mismo calibre en THHN ocupa 8.581 mm². Tres
   * conductores del 12 en THW piden tubo de ¾"; en THHN caben en ½".
   */
  aislamiento: 'thw',

  /**
   * Holgura de obra sobre el metraje que sale del plano: cortes, desperdicio y recorridos
   * que el dibujo no ve. 10% es práctica de obra, no norma — por eso es editable.
   */
  holguraObra: 1.10,

  /**
   * Reserva de espacios libres en el tablero, sobre los circuitos calculados. La norma no
   * la exige; es criterio de instalación para que la casa admita algo mañana.
   */
  reservaTablero: 0.25,

  /**
   * Metros por defecto de un circuito cuando nadie midió la distancia. Es un supuesto
   * declarado, y el sistema lo dice en pantalla cuando lo usa.
   */
  metrosPorDefecto: 20
}

/* ============================================================================
   2. INSUMOS CON IDENTIDAD TÉCNICA — la semilla
   ========================================================================== */

/**
 * Semilla del catálogo. **Los costos son de arranque y Juan los va a mover** — están en
 * pesos mexicanos sin IVA, que es como los captura el resto del sistema.
 *
 * `tec.clase` es lo que el motor busca. `costo` es lo que Juan manda.
 *
 * ⚠️ Estos precios son **estimados de arranque, no cotizaciones de proveedor.** Es la
 * única parte de este archivo que no está leída de una fuente: los datos técnicos sí
 * salen de norma y de ficha, los precios salen de que había que empezar por algo. El
 * primer pedido real los sustituye.
 */
export const INSUMOS_TECNICOS = [
  /* ---- Conductor, por calibre y aislamiento ----
     Se siembran las dos familias para que el cambio de aislamiento tenga a dónde caer. */
  /* Ojo: el 12 y el 10 en THW **no están aquí**. Ya existían en `catalogo.js` como
     `CBL-12` y `CBL-10` desde antes de esta costura, y varios productos los listan entre
     sus componentes. Se les agregó el campo `tec` allá en vez de duplicarlos: si hubiera
     dos cables del 12 en el sistema, Juan editaría uno y el otro seguiría cobrando viejo. */
  { sku: 'CBL-14-THW', nombre: 'Cable THW cal. 14 (m)', tipo: 'Consumibles', costo: 15,
    tec: { clase: 'cable', awg: '14', aislamiento: 'thw' } },
  { sku: 'CBL-8-THW', nombre: 'Cable THW cal. 8 (m)', tipo: 'Consumibles', costo: 58,
    tec: { clase: 'cable', awg: '8', aislamiento: 'thw' } },
  { sku: 'CBL-6-THW', nombre: 'Cable THW cal. 6 (m)', tipo: 'Consumibles', costo: 92,
    tec: { clase: 'cable', awg: '6', aislamiento: 'thw' } },
  { sku: 'CBL-4-THW', nombre: 'Cable THW cal. 4 (m)', tipo: 'Consumibles', costo: 145,
    tec: { clase: 'cable', awg: '4', aislamiento: 'thw' } },

  { sku: 'CBL-14-THHN', nombre: 'Cable THHN cal. 14 (m)', tipo: 'Consumibles', costo: 16,
    tec: { clase: 'cable', awg: '14', aislamiento: 'thhn' } },
  { sku: 'CBL-12-THHN', nombre: 'Cable THHN cal. 12 (m)', tipo: 'Consumibles', costo: 24,
    tec: { clase: 'cable', awg: '12', aislamiento: 'thhn' } },
  { sku: 'CBL-10-THHN', nombre: 'Cable THHN cal. 10 (m)', tipo: 'Consumibles', costo: 37,
    tec: { clase: 'cable', awg: '10', aislamiento: 'thhn' } },
  { sku: 'CBL-8-THHN', nombre: 'Cable THHN cal. 8 (m)', tipo: 'Consumibles', costo: 62,
    tec: { clase: 'cable', awg: '8', aislamiento: 'thhn' } },
  { sku: 'CBL-6-THHN', nombre: 'Cable THHN cal. 6 (m)', tipo: 'Consumibles', costo: 98,
    tec: { clase: 'cable', awg: '6', aislamiento: 'thhn' } },
  { sku: 'CBL-4-THHN', nombre: 'Cable THHN cal. 4 (m)', tipo: 'Consumibles', costo: 152,
    tec: { clase: 'cable', awg: '4', aislamiento: 'thhn' } },

  /* ---- Canalización eléctrica. `pulgadas` empata con la tabla TUBOS de `electrico.js`. */
  { sku: 'COND-12', nombre: 'Tubo conduit PVC 1/2" (m)', tipo: 'Consumibles', costo: 21,
    tec: { clase: 'conduit', pulgadas: '1/2"' } },
  { sku: 'COND-34', nombre: 'Tubo conduit PVC 3/4" (m)', tipo: 'Consumibles', costo: 29,
    tec: { clase: 'conduit', pulgadas: '3/4"' } },
  { sku: 'COND-1', nombre: 'Tubo conduit PVC 1" (m)', tipo: 'Consumibles', costo: 44,
    tec: { clase: 'conduit', pulgadas: '1"' } },
  { sku: 'COND-114', nombre: 'Tubo conduit PVC 1 1/4" (m)', tipo: 'Consumibles', costo: 68,
    tec: { clase: 'conduit', pulgadas: '1 1/4"' } },
  { sku: 'COND-112', nombre: 'Tubo conduit PVC 1 1/2" (m)', tipo: 'Consumibles', costo: 89,
    tec: { clase: 'conduit', pulgadas: '1 1/2"' } },
  { sku: 'COND-2', nombre: 'Tubo conduit PVC 2" (m)', tipo: 'Consumibles', costo: 124,
    tec: { clase: 'conduit', pulgadas: '2"' } },

  /* ---- Protecciones, por amperaje nominal ---- */
  { sku: 'PAST-15', nombre: 'Termomagnético 1 polo 15 A', tipo: 'Complementos', costo: 195,
    tec: { clase: 'proteccion', amperes: 15, polos: 1 } },
  { sku: 'PAST-20', nombre: 'Termomagnético 1 polo 20 A', tipo: 'Complementos', costo: 210,
    tec: { clase: 'proteccion', amperes: 20, polos: 1 } },
  { sku: 'PAST-30', nombre: 'Termomagnético 1 polo 30 A', tipo: 'Complementos', costo: 265,
    tec: { clase: 'proteccion', amperes: 30, polos: 1 } },
  { sku: 'PAST-2P-20', nombre: 'Termomagnético 2 polos 20 A', tipo: 'Complementos', costo: 480,
    tec: { clase: 'proteccion', amperes: 20, polos: 2 } },
  { sku: 'PAST-2P-30', nombre: 'Termomagnético 2 polos 30 A', tipo: 'Complementos', costo: 540,
    tec: { clase: 'proteccion', amperes: 30, polos: 2 } },
  { sku: 'PAST-2P-40', nombre: 'Termomagnético 2 polos 40 A', tipo: 'Complementos', costo: 620,
    tec: { clase: 'proteccion', amperes: 40, polos: 2 } },
  { sku: 'PAST-2P-50', nombre: 'Termomagnético 2 polos 50 A', tipo: 'Complementos', costo: 720,
    tec: { clase: 'proteccion', amperes: 50, polos: 2 } },

  /* ---- Boquillas de riego. `boquilla` empata con el `id` de ASPERSORES en `riego.js`. */
  { sku: 'ASP-VAN10', nombre: 'Boquilla Rain Bird 10-VAN', tipo: 'Complementos', costo: 95,
    tec: { clase: 'boquilla', boquilla: 'van-10' } },
  { sku: 'ASP-VAN12', nombre: 'Boquilla Rain Bird 12-VAN', tipo: 'Complementos', costo: 95,
    tec: { clase: 'boquilla', boquilla: 'van-12' } },
  { sku: 'ASP-VAN15', nombre: 'Boquilla Rain Bird 15-VAN', tipo: 'Complementos', costo: 98,
    tec: { clase: 'boquilla', boquilla: 'van-15' } },
  { sku: 'ASP-VAN18', nombre: 'Boquilla Rain Bird 18-VAN', tipo: 'Complementos', costo: 110,
    tec: { clase: 'boquilla', boquilla: 'van-18' } },
  { sku: 'ASP-CUERPO', nombre: 'Cuerpo emergente 4" para boquilla', tipo: 'Complementos', costo: 135,
    tec: { clase: 'cuerpo-aspersor' } },

  /* ---- Bombas. `bomba` empata con el `id` de BOMBAS en `riego.js`. */
  { sku: 'BBA-CPM600', nombre: 'Bomba Pedrollo CPm 600 · 0.5 HP', tipo: 'Complementos', costo: 3200,
    tec: { clase: 'bomba', bomba: 'cpm-600' } },
  { sku: 'BBA-CPM610', nombre: 'Bomba Pedrollo CPm 610 · 0.85 HP', tipo: 'Complementos', costo: 3900,
    tec: { clase: 'bomba', bomba: 'cpm-610' } },
  { sku: 'BBA-CPM620', nombre: 'Bomba Pedrollo CPm 620 · 1 HP', tipo: 'Complementos', costo: 4600,
    tec: { clase: 'bomba', bomba: 'cpm-620' } },
  { sku: 'BBA-CPM650', nombre: 'Bomba Pedrollo CPm 650 · 1.5 HP', tipo: 'Complementos', costo: 6400,
    tec: { clase: 'bomba', bomba: 'cpm-650' } },
  { sku: 'BBA-CPM660', nombre: 'Bomba Pedrollo CPm 660 · 2 HP', tipo: 'Complementos', costo: 8100,
    tec: { clase: 'bomba', bomba: 'cpm-660' } },
  { sku: 'BBA-CPM670', nombre: 'Bomba Pedrollo CPm 670 · 3 HP', tipo: 'Complementos', costo: 11500,
    tec: { clase: 'bomba', bomba: 'cpm-670' } },

  /* ---- Tubería hidráulica y conexiones. `pulgadas` empata con DIAMETROS de `riego.js`. */
  { sku: 'PVCH-12', nombre: 'Tubo PVC hidráulico 1/2" (m)', tipo: 'Consumibles', costo: 26,
    tec: { clase: 'tubo-hidraulico', pulgadas: '1/2"' } },
  { sku: 'PVCH-34', nombre: 'Tubo PVC hidráulico 3/4" (m)', tipo: 'Consumibles', costo: 34,
    tec: { clase: 'tubo-hidraulico', pulgadas: '3/4"' } },
  { sku: 'PVCH-1', nombre: 'Tubo PVC hidráulico 1" (m)', tipo: 'Consumibles', costo: 52,
    tec: { clase: 'tubo-hidraulico', pulgadas: '1"' } },
  { sku: 'PVCH-114', nombre: 'Tubo PVC hidráulico 1 1/4" (m)', tipo: 'Consumibles', costo: 78,
    tec: { clase: 'tubo-hidraulico', pulgadas: '1 1/4"' } },
  { sku: 'PVCH-112', nombre: 'Tubo PVC hidráulico 1 1/2" (m)', tipo: 'Consumibles', costo: 104,
    tec: { clase: 'tubo-hidraulico', pulgadas: '1 1/2"' } },

  { sku: 'CODO-34', nombre: 'Codo 90° PVC 3/4"', tipo: 'Consumibles', costo: 12,
    tec: { clase: 'codo', pulgadas: '3/4"' } },
  { sku: 'CODO-1', nombre: 'Codo 90° PVC 1"', tipo: 'Consumibles', costo: 18,
    tec: { clase: 'codo', pulgadas: '1"' } },
  { sku: 'CODO-114', nombre: 'Codo 90° PVC 1 1/4"', tipo: 'Consumibles', costo: 28,
    tec: { clase: 'codo', pulgadas: '1 1/4"' } },
  { sku: 'TE-34', nombre: 'Te PVC 3/4"', tipo: 'Consumibles', costo: 15,
    tec: { clase: 'te', pulgadas: '3/4"' } },
  { sku: 'TE-1', nombre: 'Te PVC 1"', tipo: 'Consumibles', costo: 22,
    tec: { clase: 'te', pulgadas: '1"' } },
  { sku: 'TE-114', nombre: 'Te PVC 1 1/4"', tipo: 'Consumibles', costo: 34,
    tec: { clase: 'te', pulgadas: '1 1/4"' } },

  { sku: 'VALV-ZONA', nombre: 'Válvula solenoide de zona 1"', tipo: 'Complementos', costo: 780,
    tec: { clase: 'valvula-zona' } },
  { sku: 'PROG-4', nombre: 'Programador de riego 4 estaciones', tipo: 'Complementos', costo: 1850,
    tec: { clase: 'programador', estaciones: 4 } },
  { sku: 'PROG-6', nombre: 'Programador de riego 6 estaciones', tipo: 'Complementos', costo: 2400,
    tec: { clase: 'programador', estaciones: 6 } },
  { sku: 'PROG-8', nombre: 'Programador de riego 8 estaciones', tipo: 'Complementos', costo: 3100,
    tec: { clase: 'programador', estaciones: 8 } },

  /* ---- Videovigilancia. `camara` empata con el `id` de CAMARAS en `camaras.js`. ---- */
  { sku: 'CAM-2143-28', nombre: 'Cámara Hikvision DS-2CD2143G2-I 2.8 mm', tipo: 'Complementos', costo: 2350,
    tec: { clase: 'camara', camara: 'hik-2143g2-28' } },
  { sku: 'CAM-2143-4', nombre: 'Cámara Hikvision DS-2CD2143G2-I 4 mm', tipo: 'Complementos', costo: 2350,
    tec: { clase: 'camara', camara: 'hik-2143g2-4' } },
  { sku: 'UTP-CAT6', nombre: 'Cable UTP Cat 6 exterior (m)', tipo: 'Consumibles', costo: 18,
    tec: { clase: 'utp' } },

  { sku: 'SW-POE-4', nombre: 'Switch PoE 4 puertos', tipo: 'Complementos', costo: 1450,
    tec: { clase: 'switch-poe', puertos: 4 } },
  { sku: 'SW-POE-8', nombre: 'Switch PoE 8 puertos', tipo: 'Complementos', costo: 2600,
    tec: { clase: 'switch-poe', puertos: 8 } },
  { sku: 'SW-POE-16', nombre: 'Switch PoE 16 puertos', tipo: 'Complementos', costo: 5400,
    tec: { clase: 'switch-poe', puertos: 16 } },

  { sku: 'NVR-4', nombre: 'NVR 4 canales', tipo: 'Complementos', costo: 2900,
    tec: { clase: 'nvr', canales: 4 } },
  { sku: 'NVR-8', nombre: 'NVR 8 canales', tipo: 'Complementos', costo: 4300,
    tec: { clase: 'nvr', canales: 8 } },
  { sku: 'NVR-16', nombre: 'NVR 16 canales', tipo: 'Complementos', costo: 7800,
    tec: { clase: 'nvr', canales: 16 } },

  { sku: 'HDD-2', nombre: 'Disco duro de videovigilancia 2 TB', tipo: 'Complementos', costo: 1600,
    tec: { clase: 'disco', tb: 2 } },
  { sku: 'HDD-4', nombre: 'Disco duro de videovigilancia 4 TB', tipo: 'Complementos', costo: 2500,
    tec: { clase: 'disco', tb: 4 } },
  { sku: 'HDD-6', nombre: 'Disco duro de videovigilancia 6 TB', tipo: 'Complementos', costo: 3400,
    tec: { clase: 'disco', tb: 6 } },
  { sku: 'HDD-8', nombre: 'Disco duro de videovigilancia 8 TB', tipo: 'Complementos', costo: 4200,
    tec: { clase: 'disco', tb: 8 } },

  /* ---- Wi-Fi. `ap` empata con el `id` de PUNTOS_ACCESO en `wifi.js`. ---- */
  { sku: 'AP-INT', nombre: 'Punto de acceso Wi-Fi de interior', tipo: 'Complementos', costo: 4800,
    tec: { clase: 'ap', ap: 'ap-interior' } },
  { sku: 'AP-EXT', nombre: 'Punto de acceso Wi-Fi de exterior', tipo: 'Complementos', costo: 8900,
    tec: { clase: 'ap', ap: 'ap-exterior' } },

  /* ---- Iluminación y control. `control` empata con el `id` de CONTROL en
     `iluminacion.js`. Los módulos Shelly ya existían en `catalogo.js` sin `tec`; aquí se
     dan de alta los renglones genéricos para que el motor resuelva sin depender de una
     marca concreta, y Juan puede apuntarlos a lo que distribuya. ---- */
  { sku: 'LUM-LED-8W', nombre: 'Luminaria LED empotrada 8 W · 800 lm', tipo: 'Complementos', costo: 245,
    tec: { clase: 'luminaria', lumenes: 800 } },
  { sku: 'CTRL-INT', nombre: 'Módulo de control inteligente', tipo: 'Complementos', costo: 1120,
    tec: { clase: 'control-luz', control: 'inteligente' } },
  { sku: 'CTRL-DIM', nombre: 'Módulo de atenuación inteligente', tipo: 'Complementos', costo: 1015,
    tec: { clase: 'control-luz', control: 'atenuado' } },
  { sku: 'CTRL-PRES', nombre: 'Sensor de presencia con módulo', tipo: 'Complementos', costo: 890,
    tec: { clase: 'control-luz', control: 'presencia' } }
]

/* ============================================================================
   3. RESOLUCIÓN — del requerimiento técnico al insumo de Juan
   ========================================================================== */

/**
 * Busca en el catálogo **vivo** el insumo que cumple un requerimiento técnico.
 *
 * @param insumos  la lista que salió del almacén, YA con las ediciones de Juan
 * @param criterio p. ej. `{ clase: 'cable', awg: '12', aislamiento: 'thw' }`
 *
 * Si hay varios que cumplen —dos marcas del mismo cable— gana **el más barato**, y el
 * resultado lo dice. Es una decisión automática, así que es auditable (Principio 9).
 *
 * Si no hay ninguno devuelve `null` con el criterio que no se pudo satisfacer. **No
 * inventa un precio ni cae en un genérico**: una cotización con un renglón fantasma es
 * peor que una que dice que le falta un dato.
 */
export function resolver (insumos, criterio) {
  const candidatos = (insumos || []).filter(i => {
    if (!i.tec) return false
    return Object.entries(criterio).every(([k, v]) => i.tec[k] === v)
  })

  if (!candidatos.length) {
    return { falta: true, criterio, porque: describir(criterio) }
  }

  const elegido = candidatos.reduce((a, b) => (b.costo < a.costo ? b : a))
  return {
    ...elegido,
    alternativas: candidatos.length - 1,
    porque: candidatos.length > 1
      ? `Hay ${candidatos.length} insumos que cumplen; se tomó el más barato.`
      : null
  }
}

/** Texto legible de un criterio que no se pudo satisfacer, para decirlo en pantalla. */
function describir (c) {
  if (c.clase === 'cable') return `cable del ${c.awg} en ${String(c.aislamiento).toUpperCase()}`
  if (c.clase === 'conduit') return `tubo conduit de ${c.pulgadas}`
  if (c.clase === 'tubo-hidraulico') return `tubo PVC hidráulico de ${c.pulgadas}`
  if (c.clase === 'codo') return `codo de ${c.pulgadas}`
  if (c.clase === 'te') return `te de ${c.pulgadas}`
  if (c.clase === 'boquilla') return `boquilla ${c.boquilla}`
  if (c.clase === 'bomba') return `bomba ${c.bomba}`
  if (c.clase === 'proteccion') return `interruptor de ${c.amperes} A de ${c.polos} polo(s)`
  if (c.clase === 'programador') return `programador de ${c.estaciones} estaciones`
  return c.clase
}

/**
 * La protección se compra en pasos comerciales, no en el amperaje exacto que salió del
 * cálculo. Busca el interruptor **igual o inmediato superior** que Juan tenga dado de alta.
 *
 * Ojo con la dirección: hacia ARRIBA en amperaje es hacia abajo en protección. Por eso el
 * amperaje que entra aquí ya viene topado por `proteccionPara`, que aplica el art. 240-4(d).
 */
export function resolverProteccion (insumos, amperes, polos = 1) {
  const candidatos = (insumos || [])
    .filter(i => i.tec && i.tec.clase === 'proteccion' && i.tec.polos === polos &&
                 i.tec.amperes >= amperes)
    .sort((a, b) => a.tec.amperes - b.tec.amperes)

  if (!candidatos.length) {
    return { falta: true, criterio: { clase: 'proteccion', amperes, polos },
             porque: `interruptor de ${amperes} A de ${polos} polo(s) o mayor` }
  }
  const elegido = candidatos[0]
  return {
    ...elegido,
    porque: elegido.tec.amperes > amperes
      ? `El cálculo pide ${amperes} A y el paso comercial más cercano es ${elegido.tec.amperes} A.`
      : null
  }
}

/**
 * El programador se elige por número de zonas, subiendo al modelo que las cubra.
 * Mismo criterio que la protección: no existe uno de 5 estaciones.
 */
export function resolverProgramador (insumos, zonas) {
  const candidatos = (insumos || [])
    .filter(i => i.tec && i.tec.clase === 'programador' && i.tec.estaciones >= zonas)
    .sort((a, b) => a.tec.estaciones - b.tec.estaciones)
  if (!candidatos.length) {
    return { falta: true, criterio: { clase: 'programador', estaciones: zonas },
             porque: `programador de ${zonas} estaciones o más` }
  }
  const e = candidatos[0]
  return {
    ...e,
    porque: e.tec.estaciones > zonas
      ? `Son ${zonas} zonas y el modelo más cercano trae ${e.tec.estaciones} estaciones.`
      : null
  }
}

/* ============================================================================
   4. RENGLÓN DE MATERIAL
   ========================================================================== */

/**
 * Arma un renglón de material a partir de un insumo resuelto.
 *
 * Los renglones que salen de aquí llevan **las dos justificaciones separadas**, y la
 * distinción es el corazón de este archivo:
 *
 *   · `de`      → por qué se necesita esta cantidad. Sale de la física y del plano.
 *   · `porque`  → por qué se eligió este producto. Sale del catálogo de Juan.
 *
 * Cuando el insumo falta, el renglón se genera igual pero marcado `falta`. La cantidad y
 * la razón técnica sí se conocen; lo que no hay es con qué cobrarlo. Eso se muestra como
 * pendiente, no se esconde ni se rellena con un precio inventado.
 */
export function renglon ({ insumo, cantidad, unidad, de }) {
  if (!insumo || insumo.falta) {
    return {
      concepto: `Falta dar de alta: ${insumo ? insumo.porque : 'insumo desconocido'}`,
      cantidad, unidad, de, falta: true, sku: null, costoUnitario: null
    }
  }
  return {
    concepto: insumo.nombre,
    sku: insumo.sku,
    cantidad, unidad, de,
    porque: insumo.porque || null,
    costoUnitario: insumo.costo,
    costo: insumo.costo * cantidad
  }
}
